import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Collapse, Table, Spin } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { getCostcodeBillingData } from '../../costcodes/state/costcodes.actions';
import { getIdMap, includesTerm } from '../../helpers/helpers';
import { request } from '../../helpers/requests';
import { currencyFormatter } from '../../helpers/inputParsers';

const { Panel } = Collapse;

const loadProjectCostcodesList = async (projectId) => {
  if (!projectId) return [];
  const res = await request({
    call: axios.post('/costcodes/projects', {
      projectIds: [projectId],
      active: true,
      includeCategoryIds: true,
    }),
    errMsg: 'Could not get project costcodes',
    hideSuccessToast: true,
  });
  return res?.data ?? [];
};

const loadGlobalCostcodesByDivisions = async (divisionIds = []) => {
  const uniqueDivisionIds = [...new Set((divisionIds || []).filter(Boolean))];
  if (!uniqueDivisionIds.length) return [];

  const res = await request({
    call: axios.get('/costcodes/global', {
      params: {
        divisionIds: uniqueDivisionIds,
        active: true,
        includeCategoryIds: true,
      },
    }),
    errMsg: 'Could not get global costcodes',
    hideSuccessToast: true,
  });
  if (res?.err) return null;
  return res?.data ?? [];
};

const isActiveCostcode = (cc) => (
  cc.active === undefined
  || cc.active === null
  || (cc.active !== 0 && cc.active !== false)
);

const buildRowFromCostcode = (costcode, billingById) => {
  const billing = billingById[costcode.id];
  return {
    id: costcode.id,
    code: costcode.code,
    name: costcode.name,
    projectId: costcode.projectId ?? null,
    active: costcode.active,
    categoryIds: costcode.categoryIds,
    totalHours: billing?.totalHours ?? 0,
    totalCost: billing?.totalCost ?? 0,
    totalCostHours: billing?.totalCostHours ?? 0,
    totalCostDays: billing?.totalCostDays ?? 0,
    totalBilling: billing?.totalBilling ?? 0,
    totalBillingHours: billing?.totalBillingHours ?? 0,
    totalBillingDays: billing?.totalBillingDays ?? 0,
  };
};

const buildScopedCostcodeRows = (costcodes, billingById, { projectId, globalOnly }) => {
  const rowsById = new Map();

  (costcodes || []).forEach((costcode) => {
    if (!costcode?.id) return;
    if (projectId != null && (costcode.projectId ?? null) !== projectId) return;
    if (globalOnly && costcode.projectId) return;
    rowsById.set(costcode.id, buildRowFromCostcode(costcode, billingById));
  });

  return Array.from(rowsById.values())
    .filter(isActiveCostcode)
    .filter((cc) => (globalOnly ? !cc.projectId : true))
    .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
};

const withCategoryNames = (rows, categoriesIdMap) => (
  rows.map((cc) => ({
    ...cc,
    categoryNames: cc.categoryIds?.map((categoryId) => categoriesIdMap[categoryId]?.name).join(', ') ?? '',
  }))
);

export default function CostCodeBilling({
  searchValue = '',
  dateRange,
  onColumnsChanged,
  onFiltersChanged,
  onDataChanged,
  exporter,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const projects = useSelector((state) => state.projects.projects);
  const costcodeBillingData = useSelector((state) => state.costcodes.costcodeBillingData);
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);

  const globalDivisionIds = useMemo(() => {
    const fromSelected = Array.from(selectedDivisions ?? []);
    if (fromSelected.length) return fromSelected;
    const fromProjects = new Set(projects.map((p) => p.divisionId).filter(Boolean));
    return Array.from(fromProjects);
  }, [selectedDivisions, projects]);

  const [projectCostcodes, setProjectCostcodes] = useState({});
  const [projectCostcodeLoading, setProjectCostcodeLoading] = useState({});
  const [globalCostcodes, setGlobalCostcodes] = useState(null);
  const [globalCostcodeLoading, setGlobalCostcodeLoading] = useState(false);
  const loadedProjectIdsRef = useRef(new Set());
  const globalCostcodesLoadedRef = useRef(false);

  const billingById = costcodeBillingData?.costcodes ?? {};

  const projectCostcodeRows = useMemo(() => {
    const rows = {};
    Object.entries(projectCostcodes).forEach(([projectId, costcodes]) => {
      rows[projectId] = buildScopedCostcodeRows(costcodes, billingById, { projectId });
    });
    return rows;
  }, [projectCostcodes, billingById]);

  const globalCostcodeRows = useMemo(() => {
    if (!globalCostcodes) return null;
    return buildScopedCostcodeRows(globalCostcodes, billingById, { globalOnly: true });
  }, [globalCostcodes, billingById]);

  useEffect(() => {
    const [start, end] = dateRange;
    dispatch(getCostcodeBillingData({
      startTime: start.valueOf(),
      endTime: end.valueOf(),
    }));
    setProjectCostcodes({});
    setProjectCostcodeLoading({});
    setGlobalCostcodes(null);
    setGlobalCostcodeLoading(false);
    loadedProjectIdsRef.current = new Set();
    globalCostcodesLoadedRef.current = false;
  }, [dateRange]);

  const exportCostcodeRows = useMemo(() => {
    const rows = [];
    Object.values(projectCostcodeRows).forEach((projectRows) => {
      rows.push(...projectRows);
    });
    if (globalCostcodeRows?.length) {
      rows.push(...globalCostcodeRows);
    }
    return withCategoryNames(rows, categoriesIdMap);
  }, [projectCostcodeRows, globalCostcodeRows, categoriesIdMap]);

  const loadProjectCostcodes = useCallback(async (projectId) => {
    if (!projectId || loadedProjectIdsRef.current.has(projectId)) return;
    loadedProjectIdsRef.current.add(projectId);
    setProjectCostcodeLoading((prev) => ({ ...prev, [projectId]: true }));
    try {
      const costcodes = await loadProjectCostcodesList(projectId);
      setProjectCostcodes((prev) => ({ ...prev, [projectId]: costcodes }));
    } finally {
      setProjectCostcodeLoading((prev) => ({ ...prev, [projectId]: false }));
    }
  }, []);

  const loadGlobalCostcodes = useCallback(async () => {
    if (globalCostcodesLoadedRef.current) return;
    globalCostcodesLoadedRef.current = true;
    setGlobalCostcodeLoading(true);
    try {
      const costcodes = await loadGlobalCostcodesByDivisions(globalDivisionIds);
      setGlobalCostcodes(costcodes ?? []);
    } finally {
      setGlobalCostcodeLoading(false);
    }
  }, [globalDivisionIds]);

  const decoratedProjects = useMemo(() => (
    projects.map((project) => ({
      ...project,
      ...(costcodeBillingData?.projects?.[project.id] ?? {}),
    }))
  ), [projects, costcodeBillingData]);

  const filteredProjects = useMemo(() => decoratedProjects.filter((project) => (
    includesTerm(project.name, searchValue)
    && project.active
  )), [decoratedProjects, searchValue]);

  const getProjectTableRows = useCallback((project) => {
    const rows = projectCostcodeRows[project.id] ?? [];
    return withCategoryNames(rows, categoriesIdMap);
  }, [projectCostcodeRows, categoriesIdMap]);

  const globalTableRows = useMemo(() => {
    const base = globalCostcodeRows ?? [];
    const term = searchValue?.trim() ?? '';
    const withCategories = withCategoryNames(base, categoriesIdMap);
    if (!term) return withCategories;
    return withCategories.filter((cc) => (
      includesTerm(cc.name, term)
      || includesTerm(cc.code, term)
      || includesTerm(cc.categoryNames, term)
    ));
  }, [globalCostcodeRows, categoriesIdMap, searchValue]);

  /**
   * See: https://github.com/ant-design/ant-design/issues/13825#issuecomment-449889241
   * Issue with columns not respecting width when using expandable and long words
   */
  const projectColumns = useMemo(() => [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (val) => (
        <div className="antd-table-cell-render">
          {val}
        </div>
      ),
    },
    {
      title: 'Billing Total',
      dataIndex: 'totalBilling',
      key: 'totalBilling',
      width: 150,
      render: (val) => (
        <div className="antd-table-cell-render">
          {currencyFormatter(val)}
        </div>
      ),
    },
    {
      title: 'Cost Total',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 150,
      render: (val) => (
        <div className="antd-table-cell-render">
          {currencyFormatter(val)}
        </div>
      ),
    },
    {
      title: 'Profit',
      dataIndex: 'profit',
      key: 'profit',
      width: 150,
      render: (_, record) => (
        <div className="antd-table-cell-render">
          {currencyFormatter(
            (record.totalBilling ?? 0) - (record.totalCost ?? 0),
          )}
        </div>
      ),
    },
  ], []);

  const costcodeColumns = useMemo(() => [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 75,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 80,
    },
    {
      title: 'Categories',
      dataIndex: 'categoryNames',
      key: 'categoryNames',
      width: 100,
    },
    {
      title: 'Billed Hours',
      dataIndex: 'totalBillingHours',
      key: 'totalBillingHours',
      width: 80,
      render: (val) => (
        <div className="antd-table-cell-render">
          {`${(val ?? 0).toFixed(2)} hours`}
        </div>
      ),
    },
    {
      title: 'Billed Days',
      dataIndex: 'totalBillingDays',
      key: 'totalBillingDays',
      width: 80,
      render: (val) => (
        <div className="antd-table-cell-render">
          {`${(val ?? 0).toFixed(2)} days`}
        </div>
      ),
    },
    {
      title: 'Billed Amount',
      dataIndex: 'totalBilling',
      key: 'totalBilling',
      width: 80,
      render: (val) => (
        <div className="antd-table-cell-render">
          {currencyFormatter(val)}
        </div>
      ),
    },
    {
      title: 'Cost Hours',
      dataIndex: 'totalCostHours',
      key: 'totalCostHours',
      width: 80,
      render: (val) => (
        <div className="antd-table-cell-render">
          {`${(val ?? 0).toFixed(2)} hours`}
        </div>
      ),
    },
    {
      title: 'Cost Days',
      dataIndex: 'totalCostDays',
      key: 'totalCostDays',
      width: 80,
      render: (val) => (
        <div className="antd-table-cell-render">
          {`${(val ?? 0).toFixed(2)} days`}
        </div>
      ),
    },
    {
      title: 'Cost Amount',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 80,
      render: (val) => (
        <div className="antd-table-cell-render">
          {currencyFormatter(val)}
        </div>
      ),
    },
    {
      title: 'Profit',
      dataIndex: 'profit',
      key: 'profit',
      width: 80,
      render: (_, record) => (
        <div className="antd-table-cell-render">
          {currencyFormatter(
            (record.totalBilling ?? 0) - (record.totalCost ?? 0),
          )}
        </div>
      ),
    },
  ], []);

  useEffect(() => {
    if (!exporter) return false;

    exporter.setTopLevelColumns([projectColumns.map((column) => column.title)]);
    const projectExportRows = [];
    filteredProjects.forEach((project) => {
      const row = [
        project.name,
        currencyFormatter(project.totalBilling),
        currencyFormatter(project.totalCost),
        currencyFormatter((project.totalBilling ?? 0) - (project.totalCost ?? 0)),
      ];
      projectExportRows.push(row);
    });

    exporter.setTopLevelData(projectExportRows);
    exporter.updateSettings({
      hideBodyRow: true,
      hideSummaryRow: true,
    });

    return () => {
      exporter.resetTopLevel();
      exporter.updateSettings({
        hideBodyRow: false,
        hideSummaryRow: false,
      });
    };
  }, [exporter, projectColumns, filteredProjects]);

  useEffect(() => {
    onFiltersChanged({});
  }, [onFiltersChanged]);

  const projectMap = useMemo(() => getIdMap(projects), [projects]);

  useEffect(() => {
    onColumnsChanged(['Project'].concat(costcodeColumns.map((column) => column.title)));
    onDataChanged(exportCostcodeRows.map((costcode) => [
      projectMap[costcode.projectId]?.name ?? 'Global',
      costcode.code,
      costcode.name,
      costcode.categoryNames ?? '',
      `${(costcode.totalBillingHours ?? 0).toFixed(2)} hours`,
      `${(costcode.totalBillingDays ?? 0).toFixed(2)} days`,
      currencyFormatter(costcode.totalBilling),
      `${(costcode.totalCostHours ?? 0).toFixed(2)} hours`,
      currencyFormatter(costcode.totalCost),
      currencyFormatter((costcode.totalBilling ?? 0) - (costcode.totalCost ?? 0)),
    ]));
  }, [
    onColumnsChanged,
    onDataChanged,
    costcodeColumns,
    exportCostcodeRows,
    projectMap,
  ]);

  const handleProjectExpand = useCallback((expanded, project) => {
    if (expanded && project?.id) {
      loadProjectCostcodes(project.id);
    }
  }, [loadProjectCostcodes]);

  const handleCollapseChange = useCallback((keys) => {
    if (keys.includes('global')) {
      loadGlobalCostcodes();
    }
  }, [loadGlobalCostcodes]);

  const expandedRowRender = useCallback((project) => {
    if (projectCostcodeLoading[project.id]) {
      return (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <Spin size="small" />
        </div>
      );
    }
    return (
      <Table
        columns={costcodeColumns}
        dataSource={getProjectTableRows(project)}
        pagination={false}
        size="small"
        rowKey="id"
      />
    );
  }, [costcodeColumns, getProjectTableRows, projectCostcodeLoading]);

  return (
    <div style={{ width: '100%', height: 'calc(100% - 50px)', overflowY: 'scroll' }}>
      <Collapse defaultActiveKey={['project']} onChange={handleCollapseChange}>
        <Panel header={`${t('Project')} Codes`} key="project">
          <Table
            columns={projectColumns}
            dataSource={filteredProjects}
            expandable={{ expandedRowRender, onExpand: handleProjectExpand }}
            rowKey="id"
            pagination={false}
            style={{ width: '100%' }}
          />
        </Panel>
        <Panel header="Global Codes" key="global">
          {globalCostcodeLoading ? (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <Spin size="small" />
            </div>
          ) : (
            <Table
              columns={costcodeColumns}
              dataSource={globalTableRows}
              rowKey="id"
              pagination={false}
              style={{ width: '100%' }}
            />
          )}
        </Panel>
      </Collapse>
    </div>
  );
}

CostCodeBilling.propTypes = {
  searchValue: PropTypes.string,
  dateRange: PropTypes.arrayOf(PropTypes.shape({})),
  exporter: PropTypes.shape({
    setTopLevelColumns: PropTypes.func,
    setTopLevelData: PropTypes.func,
    updateSettings: PropTypes.func,
    resetTopLevel: PropTypes.func,
  }),
};

CostCodeBilling.defaultProps = {
  searchValue: '',
  dateRange: [],
  exporter: null,
};
